import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useHotkeyActions, useHotkeyScope } from '@/shared/lib/hotkeys';

export interface UseSelectableRowOptions<T> {
  /** Открыть строку (то же, что кнопка «Открыть» и двойной клик) — вызывается по `F2` и `Enter`. */
  onOpen: (item: T) => void;
  /** Главное действие «Новый»/«Создать» страницы — `Insert`. Страницы без создания «на этом
   *  экране» (например, список заявок клиентов) его не передают. */
  onCreate?: () => void;
  /** Название главного действия создания — в подсказке кнопки и в строке внизу. */
  createLabel?: string;
  /** Причина, по которой создавать сейчас нельзя (кнопка отключена, клавиша ничего не делает). */
  createDisabled?: string;
  /** Второй вид создания — `Shift+Insert` (в кассовых ордерах: приходный — `Insert`, расходный —
   *  `Shift+Insert`). */
  onCreateSecondary?: () => void;
  createSecondaryLabel?: string;
  /** Экспорт списка — `F9`; `exporting` блокирует повторный запуск, пока идёт выгрузка. */
  onExport?: () => void;
  exporting?: boolean;
  /** «Загрузить ещё» — `Alt+L`. Передают, только пока есть что догружать. */
  onLoadMore?: () => void;
  /** Перевести курсор к фильтрам — `F3`; сброс фильтров — `Shift+F3` (`filtersEmpty` — сбрасывать
   *  нечего). */
  onFocusFilters?: () => void;
  onResetFilters?: () => void;
  filtersEmpty?: boolean;
  /** При открытии страницы ставить курсор на таблицу, чтобы стрелки и `Enter` работали сразу
   *  (после перехода по меню фокус стоял бы на пункте меню). Окнам не нужно: у них свой начальный
   *  фокус (`data-autofocus`). По умолчанию включено. */
  autoFocus?: boolean;
}

/** Сколько строк пропускают `PageUp`/`PageDown`. */
const PAGE_ROWS = 10;

/**
 * Общий паттерн списочных страниц и окон со списками, в 1С-стиле (см. CLAUDE.md): выделенная
 * строка подсвечивается; стрелки, `Home`/`End`, `PgUp`/`PgDn` двигают выделение, `Enter` и `F2`
 * открывают строку; `Insert`, `Shift+Insert`, `F9`, `Alt+L`, `F3`, `Shift+F3` — создание, экспорт,
 * «Загрузить ещё», переход к фильтрам и их сброс. Клик по строке выделяет её, двойной клик открывает.
 *
 * Возвращает всё нужное таблице: `rowProps` для `onRow` (подсветка и клики), `containerProps` для
 * обёртки вокруг `<Table>` (она принимает фокус и стрелки; на ней же стоит курсор после закрытия
 * окна, которое открыли с этой страницы), `actions.get(id)` для кнопок (`HotkeyButton`) и
 * `bindings` для `<HotkeyHint>`.
 */
export function useSelectableRow<T extends { id: string }>(
  items: T[],
  {
    onOpen,
    onCreate,
    createLabel,
    createDisabled,
    onCreateSecondary,
    createSecondaryLabel,
    onExport,
    exporting,
    onLoadMore,
    onFocusFilters,
    onResetFilters,
    filtersEmpty,
    autoFocus = true,
  }: UseSelectableRowOptions<T>,
  /** `false` — не регистрировать горячие клавиши (например, неактивная вкладка `Tabs`, где
   *  панели остаются смонтированными, но взаимодействовать с ними сейчас нельзя). */
  enabled = true,
) {
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const selected = items.find((i) => i.id === selectedId);

  const { bindings, get } = useHotkeyActions({
    ...(onCreate ? { 'list.create': { label: createLabel, run: onCreate, disabled: createDisabled ?? false } } : {}),
    ...(onCreateSecondary ? { 'list.createSecondary': { label: createSecondaryLabel, run: onCreateSecondary } } : {}),
    'list.open': { run: () => selected && onOpen(selected), disabled: selected ? false : 'Выберите строку в списке' },
    ...(onExport ? { 'list.export': { run: onExport, disabled: exporting ? 'Выгрузка уже выполняется' : false } } : {}),
    ...(onLoadMore ? { 'list.loadMore': { run: onLoadMore, hint: false } } : {}),
    ...(onFocusFilters ? { 'list.filters': { run: onFocusFilters, hint: false } } : {}),
    ...(onResetFilters ? { 'list.resetFilters': { run: onResetFilters, hint: false, disabled: filtersEmpty ? 'Фильтры не заданы' : false } } : {}),
  });
  useHotkeyScope(bindings, enabled);

  // Выделение сдвинули клавишей — подводим к нему прокрутку; кликом не нужно: строка и так под мышью.
  const container = useRef<HTMLElement | null>(null);
  const movedByKeyboard = useRef(false);
  useEffect(() => {
    if (!movedByKeyboard.current || !selectedId) return;
    movedByKeyboard.current = false;
    for (const row of container.current?.querySelectorAll<HTMLElement>('[data-row-key]') ?? []) {
      if (row.dataset.rowKey === selectedId) row.scrollIntoView?.({ block: 'nearest' });
    }
  }, [selectedId]);

  const attach = useCallback((el: HTMLElement | null) => {
    container.current = el;
  }, []);

  // Курсор на таблицу при открытии страницы — но не отбираем его у поля ввода или у окна.
  useEffect(() => {
    const el = container.current;
    if (!autoFocus || !enabled || !el) return;
    const active = document.activeElement;
    if (!active || active === document.body || active.closest('.ant-menu, .ant-layout-sider')) el.focus({ preventScroll: true });
    // Только при монтировании: дальше курсор принадлежит пользователю.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    // Только когда курсор на самой таблице: внутри ячейки могут быть кнопки и поля со своими клавишами.
    if (e.target !== e.currentTarget || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (e.key === 'Enter') {
      if (selected) {
        e.preventDefault();
        onOpen(selected);
      }
      return;
    }
    const last = items.length - 1;
    const index = items.findIndex((i) => i.id === selectedId);
    const target: Record<string, number> = {
      ArrowDown: index < 0 ? 0 : Math.min(index + 1, last),
      ArrowUp: index < 0 ? 0 : Math.max(index - 1, 0),
      PageDown: index < 0 ? 0 : Math.min(index + PAGE_ROWS, last),
      PageUp: index < 0 ? 0 : Math.max(index - PAGE_ROWS, 0),
      Home: 0,
      End: last,
    };
    const next = target[e.key];
    if (next === undefined) return;
    e.preventDefault(); // стрелки не должны прокручивать страницу
    const item = items[next];
    if (!item) return;
    movedByKeyboard.current = true;
    setSelectedId(item.id);
  }

  return {
    selectedId,
    setSelectedId,
    selected,
    rowProps: (record: T) => ({
      onClick: () => {
        movedByKeyboard.current = false;
        setSelectedId(record.id);
      },
      onDoubleClick: () => onOpen(record),
      className: record.id === selectedId ? 'row-selected' : undefined,
      'aria-selected': record.id === selectedId,
    }),
    /** Свойства обёртки вокруг таблицы: фокус, стрелки, `Enter`. */
    containerProps: { ref: attach, tabIndex: 0, onKeyDown, className: 'list-keyboard' },
    /** Действие для кнопки: `actions.get('list.open')` и т. д. */
    actions: { get },
    bindings,
  };
}
